import { useEffect, useState } from "react";
import { Check, CreditCard, ExternalLink, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export default function Billing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data)).catch(() => setPlans([]));
    api.get("/billing/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);

  const checkout = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const r = await api.post("/billing/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(errorMessage(e)); setBusy(""); }
  };

  const portal = async () => {
    setBusy("portal");
    try {
      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) { toast.error(errorMessage(e)); setBusy(""); }
  };

  const active = me?.access?.active;
  const sub = me?.subscription || {};

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-16" data-testid="billing-page">
      <div className="space-y-3 mb-10">
        <p className="eyebrow">Billing</p>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">{active ? "Your plan" : `Welcome, ${user?.name?.split(" ")[0] || "there"} — choose a plan`}</h1>
        <p className="text-base md:text-lg text-slate-600 max-w-2xl">
          {active ? "Manage your subscription, invoices and payment method." : "Your license is approved. Pick a plan to unlock the library for you and your clients. Payments are handled securely by Stripe."}
        </p>
      </div>

      {me && sub?.plan && (
        <div className={`rounded-3xl border p-6 sm:p-8 mb-10 flex flex-wrap items-center justify-between gap-4 ${active ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="billing-current">
          <div className="flex items-center gap-4">
            <span className={`w-12 h-12 rounded-2xl grid place-items-center ${active ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"}`}><ShieldCheck className="w-6 h-6" /></span>
            <div>
              <p className="font-display text-xl font-bold text-slate-900" data-testid="billing-plan-name">{me.plan?.name || sub.plan}</p>
              <p className="text-sm text-slate-600">Status: <span className="font-semibold capitalize" data-testid="billing-status">{sub.status}</span>{me.plan ? ` · $${me.plan.price}/${me.plan.interval}` : ""}</p>
            </div>
          </div>
          {sub.stripe_customer_id && <Button variant="outline" className="rounded-full" onClick={portal} disabled={busy === "portal"} data-testid="billing-portal-button"><CreditCard className="w-4 h-4" /> Manage payment & invoices <ExternalLink className="w-3 h-3" /></Button>}
        </div>
      )}

      {!plans ? <div className="grid md:grid-cols-3 gap-6">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-80 rounded-3xl" />)}</div> : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="billing-plans">
          {plans.map((p) => {
            const current = sub.plan === p.lookup_key && active;
            return (
              <div key={p.lookup_key} className={`rounded-3xl p-7 border flex flex-col ${p.highlight ? "bg-slate-900 text-white border-slate-900 shadow-xl" : "bg-white border-slate-200"}`} data-testid={`billing-plan-${p.lookup_key}`}>
                <p className={`text-xs font-bold uppercase tracking-[0.2em] ${p.highlight ? "text-indigo-300" : "text-indigo-600"}`}>{p.name}</p>
                <p className="font-display text-4xl font-extrabold mt-3">${p.price}<span className={`text-base font-medium ${p.highlight ? "text-slate-400" : "text-slate-500"}`}>/{p.interval}</span></p>
                <p className={`text-sm mt-2 ${p.highlight ? "text-slate-300" : "text-slate-600"}`}>{p.blurb}</p>
                <ul className="mt-5 space-y-2 text-sm flex-1">{p.features.map((f) => <li key={f} className="flex gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0 text-emerald-500" />{f}</li>)}</ul>
                <Button onClick={() => checkout(p.lookup_key)} disabled={!!busy || current}
                  className={`mt-6 rounded-full ${p.highlight ? "bg-white text-slate-900 hover:bg-indigo-50" : "bg-indigo-600 text-white hover:bg-indigo-700"}`} data-testid={`checkout-${p.lookup_key}`}>
                  {current ? "Current plan" : busy === p.lookup_key ? "Redirecting…" : active ? "Switch to this plan" : "Subscribe"}
                </Button>
              </div>
            );
          })}
        </div>
      )}
      <p className="text-xs text-slate-500 mt-8">Test mode: use card 4242 4242 4242 4242, any future expiry and any CVC.</p>
    </div>
  );
}
